import React, { useState } from "react";
import moment from "moment";
import { Card, Typography, Pagination, Table, Input, Button, Popconfirm, message } from "antd";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { capitalizeWords } from "../../../../components/icons/ApplicationColor";
import { DeleteOutlined } from "@ant-design/icons";
import useCommonListing from "../../../../components/AdminComponents/customHooks/useCommonListing";
import axiosInstance from "../../../../api/Axios";
import CommonTopDetails from "../../../../components/AdminComponents/CommonTopDetails";
import AddLibraryModal from "./AddLibraryModal";

const { Title } = Typography;

const Library = (props) => {
    const userInfo = useSelector((state) => state.user);
    const { id } = useParams();
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [loadAgain, setLoadAgain] = useState(false);
    const { data1, canEdit } = props;

    const LoadAPI = () => {
        setLoadAgain((prev) => !prev);
    };

    const {
        data,
        itemsLength,
        loading,
        filterData,
        onShowSizeChange,
        onPageChange,
        handleSearchChange,
    } = useCommonListing({
        passSize: 15,
        passPage: 1,
        passURL: `/customer/courselibrary/?organization=${id}`,
        includeStatus: true,
        loadAgain: loadAgain,
    });

    const { pageSize, currentPage } = filterData;
    const processedData = React.useMemo(() => {
        const processItems = (items, parentId = "") => {
            return items?.map((item) => {
                const uniqueKey = parentId ? `${parentId}-${item.id}` : `${item.id}`;
                const newItem = { ...item, tableUniqueKey: uniqueKey };
                if (newItem.children && newItem.children.length > 0) {
                    newItem.children = processItems(newItem.children, uniqueKey);
                } else if (newItem.children) {
                    // Ensure empty children arrays are removed to prevent expand icons
                    delete newItem.children;
                }
                return newItem;
            });
        };
        return processItems(data);
    }, [data]);

    function getCookie(name) {
        const cookies = document.cookie.split("; ");
        for (let i = 0; i < cookies.length; i++) {
            const [cookieName, cookieValue] = cookies[i].split("=");
            if (cookieName === name) {
                return cookieValue;
            }
        }
        return null;
    }
    const csrfToken = getCookie("csrftoken");

    const handleDelete = async (record) => {
        try {
            await axiosInstance.delete(`/customer/courselibrarydelete/`, {
                params: {
                    course_id: record.id,
                    customer_id: id,
                },
                headers: {
                    "X-CSRFToken": csrfToken,
                },
            });
            message.success("Course deleted successfully from library");
            LoadAPI();
            if (props.LoadUser) {
                props.LoadUser();
            }
        } catch (err) {
            console.log(err);
            message.error("Failed to delete course from library");
        }
    };

    const columns = [
        {
            title: "Course ID",
            dataIndex: "course_id",
            key: "course_id",
            width: 150,
            fixed: "left",
        },
        {
            title: "Course Name",
            dataIndex: "name",
            key: "name",
            width: 200,
            ellipsis: true,
            fixed: "left",
        },
        {
            title: "Course Type",
            dataIndex: "course_type",
            key: "course_type",
            width: 120,
            ellipsis: true,
            render: (text) => (text ? capitalizeWords(text) : " "),
        },
        {
            title: "Category",
            dataIndex: "category",
            key: "category",
            width: 150,
        },
        {
            title: "Language",
            dataIndex: "language",
            key: "language",
            width: 100,
        },
        {
            title: "Mode",
            dataIndex: "mode",
            key: "mode",
            width: 120,
        },
        {
            title: "Created At",
            dataIndex: "created_at",
            key: "created_at",
            width: 150,
            render: (text) => (text ? moment(text).format("MMM-DD-YYYY") : "-"),
        },
        {
            title: capitalizeWords("actions"),
            key: "actions",
            align: "center",
            width: 100,
            fixed: "right",
            onCell: (record) => ({
                onClick: (e) => {
                    e.stopPropagation();
                    if (canEdit === false) {
                        // Additional safety for click handling
                    }
                },
            }),
            render: (_, record) => (
                <Popconfirm
                    title="Are you sure you want to delete this course?"
                    onConfirm={() => canEdit !== false && handleDelete(record)}
                    okText="Yes"
                    cancelText="No"
                    disabled={canEdit === false}
                    placement="leftTop"
                >
                    <DeleteOutlined
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            cursor: canEdit === false ? "not-allowed" : "pointer",
                            fontSize: "1.3rem",
                            color: canEdit === false ? "#d9d9d9" : "#ff4d4f",
                            opacity: canEdit === false ? 0.5 : 1,
                        }}
                    />
                </Popconfirm>
            ),
        },
    ];

    return (
        <Card style={{ width: "100%", marginBottom: "10px" }} hoverable>
            <Title level={5} style={{ marginInline: "10px" }}>
                Library
            </Title>
            <CommonTopDetails
                title="Course Library"
                titleSuffix={`${itemsLength || 0} item(s)`}
                searchPlaceholder="Search..."
                searchAction={handleSearchChange}
                showAddButton={true}
                addButtonProps={{
                    title: "Add Course",
                    modalType: "PopUP",
                    modalProps: () => setIsAddModalOpen(true),
                    disabled: canEdit === false,
                }}
                filterAction={false}
                marginPass="15px"
                paddingInlinepass="10px"
            />
            <Table
                columns={columns}
                dataSource={processedData}
                rowKey="tableUniqueKey"
                size="small"
                loading={loading}
                className="table-container"
                style={{ margin: "1rem" }}
                pagination={false}
                scroll={{
                    x: 1000,
                }}
                bordered
                expandable={{
                    rowExpandable: (record) =>
                        record.children && record.children.length > 0,
                }}
            />
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    padding: "1rem",
                }}
            >
                <Pagination
                    size="small"
                    showSizeChanger
                    onShowSizeChange={onShowSizeChange}
                    onChange={onPageChange}
                    pageSizeOptions={["5", "10", "15", "20", "50"]}
                    current={currentPage}
                    defaultPageSize={pageSize}
                    total={itemsLength}
                />
            </div>
            {isAddModalOpen && (
                <AddLibraryModal
                    open={isAddModalOpen}
                    onCancel={() => setIsAddModalOpen(false)}
                    onSuccess={() => {
                        LoadAPI();
                        if (props.LoadUser) {
                            props.LoadUser();
                        }
                    }}
                    customerId={id}
                    parentKeyId={userInfo?.data?.organization?.id}
                />
            )}
        </Card>
    );
};

export default Library;
